import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../../layouts/PageTitle";
import { updateDailyMaterialReportApi } from "../../../../services/dailyMaterialService";
import {
  useGetWorkProgressDropdownsQuery,
  useGetDailyMaterialReportQuery,
} from "../../../../store/appApi";
import { setFormDirty } from "../../../../store/projectSlice";
import { selectHasModuleAccess } from "../../../../store/authSlice";

const EMPTY_MANUAL_ROW = {
  materialId: "",
  materialName: "",
  measurement: "",
  qtyIssued: "",
  qtyReturned: "",
  remarks: "",
  issuedToContractorId: "",
  balanceSnapshot: "",
  fromMorningReport: false,
};

const EditDailyMaterial = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasWrite = useSelector(selectHasModuleAccess("workProgress", "read-write"));

  const [rows, setRows] = useState(null); // null = not yet seeded
  const [summaryMeta, setSummaryMeta] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const { data: dropdowns } = useGetWorkProgressDropdownsQuery();
  const materials = dropdowns?.materials ?? [];
  const contractors = dropdowns?.contractors ?? [];

  const { data: reportData, isLoading, isError } = useGetDailyMaterialReportQuery(id);
  const r = reportData?.report;

  // Seed rows once from existing report
  useEffect(() => {
    if (!r || rows !== null) return;
    const seeded = (r.materials ?? []).map((m) => ({
      materialId:           String(m.materialId?._id ?? m.materialId ?? ""),
      materialName:         m.materialId?.skuName ?? m.materialName ?? "",
      measurement:          m.materialId?.measurement ?? m.measurement ?? "",
      qtyIssued:            String(m.qtyIssued ?? 0),
      qtyReturned:          String(m.qtyReturned ?? ""),
      remarks:              m.remarks ?? "",
      issuedToContractorId: String(m.issuedToContractorId?._id ?? m.issuedToContractorId ?? ""),
      issuedToContractorName: m.issuedToContractorId?.name ?? "",
      balanceSnapshot:      String(m.balanceSnapshot ?? ""),
      fromMorningReport:    !!m.fromMorningReport,
    }));

    // Seed summaryMeta from existing summary
    const meta = {};
    (r.summary ?? []).forEach((s) => {
      const key = String(s.materialId?._id ?? s.materialId ?? "");
      if (key) meta[key] = { remarks: s.remarks ?? "" };
    });

    setRows(seeded);
    setSummaryMeta(meta);
  }, [r, rows]);

  useEffect(() => {
    dispatch(setFormDirty(rows !== null && rows.length > 0));
    return () => dispatch(setFormDirty(false));
  }, [rows, dispatch]);

  const updateRow = useCallback(
    (index, field, value) => {
      setRows((prev) =>
        prev.map((row, i) => {
          if (i !== index) return row;
          const updated = { ...row, [field]: value };
          if (field === "materialId" && !row.fromMorningReport) {
            const mat = materials.find((m) => String(m._id) === value);
            if (mat) {
              updated.materialName = mat.skuName;
              updated.measurement = mat.measurement ?? "";
            }
          }
          return updated;
        }),
      );
    },
    [materials],
  );

  const addManualRow = useCallback(() => {
    setRows((prev) => [...prev, { ...EMPTY_MANUAL_ROW }]);
  }, []);

  const removeRow = useCallback((index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateSummaryMeta = (materialId, value) => {
    setSummaryMeta((prev) => ({
      ...prev,
      [materialId]: { remarks: value },
    }));
  };

  const summaryRows = useMemo(() => {
    if (!rows) return [];
    const map = {};
    rows.forEach((r) => {
      if (!r.materialId) return;
      const key = r.materialId;
      if (!map[key]) {
        const mat = materials.find((m) => String(m._id) === key);
        map[key] = {
          materialId: key,
          materialName: r.materialName || mat?.skuName || key,
          measurement: r.measurement || mat?.measurement || "",
          qtyIssued: 0,
          qtyReturned: 0,
        };
      }
      map[key].qtyIssued += Number(r.qtyIssued || 0);
      map[key].qtyReturned += Number(r.qtyReturned || 0);
    });
    return Object.values(map);
  }, [rows, materials]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    const filledRows = (rows ?? []).filter((r) => r.materialId);
    if (filledRows.length === 0) errs.rows = "At least one material entry is required.";
    filledRows.forEach((r, i) => {
      const ret = Number(r.qtyReturned || 0);
      const issued = Number(r.qtyIssued || 0);
      if (ret < 0) errs[`ret_${i}`] = "Cannot be negative.";
      if (issued > 0 && ret > issued) errs[`ret_${i}`] = "Cannot exceed qty issued.";
    });
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const summary = summaryRows.map((s) => ({
      materialId:   s.materialId,
      materialName: s.materialName,
      measurement:  s.measurement,
      qtyIssued:    s.qtyIssued,
      qtyReturned:  s.qtyReturned,
      remarks:      summaryMeta[s.materialId]?.remarks ?? "",
    }));

    setSubmitting(true);
    try {
      await updateDailyMaterialReportApi(id, { materials: filledRows, summary });
      dispatch(setFormDirty(false));
      await Swal.fire({ icon: "success", title: "Report updated!", timer: 1500, showConfirmButton: false });
      navigate(`/progress/daily-material/detail/${id}`);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err?.response?.data?.message || "Failed to update report." });
    } finally {
      setSubmitting(false);
    }
  };

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { dateStyle: "long" }) : "—";

  if (!hasWrite) {
    return (
      <>
        <PageTitle activeMenu="Edit Daily Report" motherMenu="Daily Progress" />
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="fa fa-lock fa-3x text-warning mb-3 d-block" />
            <h5>Access Restricted</h5>
          </div>
        </div>
      </>
    );
  }

  if (isLoading || rows === null) {
    return (
      <>
        <PageTitle activeMenu="Edit Daily Report" motherMenu="Daily Progress" />
        <div className="text-center py-5"><span className="spinner-border" /></div>
      </>
    );
  }

  if (isError || !r) {
    return (
      <>
        <PageTitle activeMenu="Edit Daily Report" motherMenu="Daily Progress" />
        <div className="alert alert-danger">Failed to load report.</div>
      </>
    );
  }

  const hasMorningReport = !!r.morningReportId;

  return (
    <>
      <PageTitle activeMenu="Edit Daily Report" motherMenu="Daily Progress" />
      <form onSubmit={handleSubmit}>
        {/* Date header (read-only) */}
        <div className="row mb-4">
          <div className="col-lg-5">
            <div className="card">
              <div className="card-body py-3 d-flex align-items-center gap-4">
                <div>
                  <span className="text-muted small d-block">Report Date</span>
                  <span className="fw-bold fs-5">{fmtDate(r.date)}</span>
                </div>
                <div>
                  <span className="text-muted small d-block">Morning Report</span>
                  {hasMorningReport ? (
                    <span className="badge bg-success px-3 py-2">Linked</span>
                  ) : (
                    <span className="badge bg-warning text-dark px-3 py-2">Not linked</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Material table */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="card">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div>
                  <h5 className="card-title mb-0">Material Entries</h5>
                  <small className="text-muted">
                    Morning report rows are locked — edit Qty Returned and Remarks freely
                  </small>
                </div>
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={addManualRow}>
                  + Add More Items
                </button>
              </div>

              {errors.rows && (
                <div className="alert alert-danger mx-3 mt-3 mb-0">{errors.rows}</div>
              )}

              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-bordered align-middle mb-0" style={{ minWidth: 820 }}>
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 36 }}>#</th>
                        <th>Material</th>
                        <th>Issued To</th>
                        <th style={{ width: 70 }}>Unit</th>
                        <th style={{ width: 105 }}>Qty Issued</th>
                        <th style={{ width: 120 }}>Qty Returned</th>
                        {hasMorningReport && <th style={{ width: 90 }}>Balance</th>}
                        <th>Remarks</th>
                        <th style={{ width: 36 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, i) => (
                        <tr key={i}>
                          <td className="text-muted small">{i + 1}</td>

                          {/* Material */}
                          <td>
                            {row.fromMorningReport ? (
                              <span className="fw-semibold">{row.materialName || "—"}</span>
                            ) : (
                              <select
                                className="form-select form-select-sm"
                                value={row.materialId}
                                onChange={(e) => updateRow(i, "materialId", e.target.value)}
                              >
                                <option value="">— Select —</option>
                                {materials.map((m) => (
                                  <option key={m._id} value={m._id}>
                                    {m.skuName}{m.measurement ? ` (${m.measurement})` : ""}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>

                          {/* Issued To */}
                          <td>
                            {row.fromMorningReport ? (
                              <span className="text-muted small">{row.issuedToContractorName || "—"}</span>
                            ) : (
                              <select
                                className="form-select form-select-sm"
                                value={row.issuedToContractorId}
                                onChange={(e) => updateRow(i, "issuedToContractorId", e.target.value)}
                              >
                                <option value="">—</option>
                                {contractors.map((c) => (
                                  <option key={c._id} value={c._id}>{c.name}</option>
                                ))}
                              </select>
                            )}
                          </td>

                          {/* Unit */}
                          <td className="text-muted small">{row.measurement || "—"}</td>

                          {/* Qty Issued */}
                          <td>
                            {row.fromMorningReport ? (
                              <span className="badge badge-danger light px-3">{row.qtyIssued}</span>
                            ) : (
                              <input
                                type="number"
                                min="0"
                                className="form-control form-control-sm"
                                value={row.qtyIssued}
                                onChange={(e) => updateRow(i, "qtyIssued", e.target.value)}
                                placeholder="0"
                              />
                            )}
                          </td>

                          {/* Qty Returned */}
                          <td>
                            <input
                              type="number"
                              min="0"
                              className={`form-control form-control-sm ${errors[`ret_${i}`] ? "is-invalid" : ""}`}
                              value={row.qtyReturned}
                              onChange={(e) => {
                                updateRow(i, "qtyReturned", e.target.value);
                                setErrors((p) => ({ ...p, [`ret_${i}`]: "" }));
                              }}
                              placeholder="0"
                            />
                            {errors[`ret_${i}`] && (
                              <div className="invalid-feedback">{errors[`ret_${i}`]}</div>
                            )}
                          </td>

                          {/* Balance snapshot */}
                          {hasMorningReport && (
                            <td className="text-muted small text-center">
                              {row.fromMorningReport ? row.balanceSnapshot || "—" : "—"}
                            </td>
                          )}

                          {/* Remarks */}
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={row.remarks}
                              onChange={(e) => updateRow(i, "remarks", e.target.value)}
                              placeholder="Remarks"
                            />
                          </td>

                          {/* Remove (manual only) */}
                          <td className="text-center">
                            {!row.fromMorningReport && (
                              <button
                                type="button"
                                className="btn btn-xs btn-outline-danger"
                                onClick={() => removeRow(i)}
                              >
                                ×
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {rows.length === 0 && (
                        <tr>
                          <td colSpan={hasMorningReport ? 9 : 8} className="text-center py-4 text-muted">
                            No entries yet.{" "}
                            <button type="button" className="btn btn-sm btn-link p-0" onClick={addManualRow}>
                              Add one
                            </button>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Summary */}
        {summaryRows.length > 0 && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Daily Summary</h5>
                  <small className="text-muted">One row per material SKU · totals aggregated</small>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-bordered align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Material</th>
                          <th>Unit</th>
                          <th>Total Qty Issued</th>
                          <th>Total Qty Returned</th>
                          <th>Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summaryRows.map((s, i) => (
                          <tr key={s.materialId}>
                            <td className="text-muted small">{i + 1}</td>
                            <td className="fw-semibold">{s.materialName}</td>
                            <td className="text-muted small">{s.measurement || "—"}</td>
                            <td>
                              <span className="badge badge-danger light">{s.qtyIssued}</span>
                            </td>
                            <td>
                              <span className="badge badge-success light">{s.qtyReturned}</span>
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Remarks"
                                value={summaryMeta[s.materialId]?.remarks ?? ""}
                                onChange={(e) => updateSummaryMeta(s.materialId, e.target.value)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="row">
          <div className="col-12 d-flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? (
                <><span className="spinner-border spinner-border-sm me-2" />Saving…</>
              ) : (
                "Save Changes"
              )}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate(`/progress/daily-material/detail/${id}`)}
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

export default EditDailyMaterial;
